import { z } from 'zod';
import type { DependencyType } from './scheduling';

/**
 * Importing a plan that already exists.
 *
 * A real project does not arrive empty. It arrives with a programme somebody
 * has already written -- in a scheduling tool, in a spreadsheet, in a
 * consultant's deliverable -- and until this existed, adopting the product
 * meant retyping it. That is the single largest reason a tool like this is
 * evaluated and then not used.
 *
 * ## The same shape as the cost import, deliberately
 *
 * CSV as text in a JSON body rather than an upload; a dry run that reports what
 * would happen before anything is written; a commit that is one transaction, so
 * an import cannot half-succeed and leave a plan nobody can reconcile against
 * the file it came from. Those decisions were made for the cost ledger
 * (docs/decisions.md) and none of the reasoning is about money.
 *
 * ## Identifiers are local to the file
 *
 * A schedule task has no business key in this product -- deliberately, since a
 * WBS code is a property of one plan and not of the work. So `id` in the file
 * means only "the row `parent_id` and `predecessors` point at", and is resolved
 * to real identifiers during the commit. Nothing about it is stored, and two
 * imports may reuse the same codes without colliding.
 *
 * ## Create-only, and the rejection says so
 *
 * An import never updates an existing task. That follows the change-session
 * rule rather than dodging it: creating a task is exempt from needing an open
 * session, because a task nobody depends on yet cannot displace anybody, while
 * *moving* one propagates through every successor. An import that silently
 * rewrote dates would be the largest possible unreviewed edit.
 *
 * Re-importing a revised programme is a real workflow and is deliberately not
 * this feature -- it is a diff-and-review problem, not an import one, and
 * pretending otherwise would ship the dangerous half of it.
 */

/* --------------------------------------------------------------- the file */

/**
 * What the file must carry, and what it may.
 *
 * `id` and `name` are the whole of the required set: a plan with nothing but
 * names and a hierarchy is still worth importing, and the engine already
 * reports a task with no duration as unscheduled rather than refusing it.
 */
export const SCHEDULE_IMPORT_REQUIRED_COLUMNS = ['id', 'name'] as const;

export const SCHEDULE_IMPORT_OPTIONAL_COLUMNS = [
  'parent_id',
  'duration_days',
  'earliest_start',
  'deadline',
  'item_type',
  'predecessors',
  'stage',
  'section',
  'description',
] as const;

export const scheduleImportSchema = z
  .object({
    /** The file, as text. Two million characters is roughly a 20 000-row plan. */
    csv: z.string().min(1).max(2_000_000),
    /** When false the response reports what would happen and writes nothing. */
    commit: z.boolean().default(false),
  })
  .strict();
export type ScheduleImport = z.infer<typeof scheduleImportSchema>;

/* -------------------------------------------------------------- the result */

export const scheduleImportRowErrorSchema = z.object({
  row: z.number().int().positive(),
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type ScheduleImportRowError = z.infer<typeof scheduleImportRowErrorSchema>;

export const scheduleImportResultSchema = z.object({
  committed: z.boolean(),
  rowsRead: z.number().int().nonnegative(),
  rowsAccepted: z.number().int().nonnegative(),
  rowsRejected: z.number().int().nonnegative(),
  /**
   * What the accepted rows would build, so a reader can recognise their own
   * plan before agreeing to it. A count of tasks says nothing; "42 tasks, 9 of
   * them milestones, 3 levels deep, 17 dependencies" is a plan somebody can
   * check against the file in front of them.
   */
  taskCount: z.number().int().nonnegative(),
  milestoneCount: z.number().int().nonnegative(),
  dependencyCount: z.number().int().nonnegative(),
  maxDepth: z.number().int().nonnegative(),
  /**
   * Bounded. A file that is wrong in ten thousand ways is wrong in its first
   * few, and returning every one of them is a response nobody reads and a
   * payload that has to be paged.
   */
  errors: z.array(scheduleImportRowErrorSchema).max(200),
});
export type ScheduleImportResult = z.infer<typeof scheduleImportResultSchema>;

/* --------------------------------------------------------- dependency refs */

/**
 * How a predecessor is written in a cell: `a12`, or `a12:FS+3`.
 *
 * The type and lag are optional because most rows do not need them, and the
 * default is the one the engine already treats as ordinary: finish-to-start
 * with no lag. Separated by spaces or semicolons so a spreadsheet's own comma
 * handling never has to be reasoned about -- a cell holding `a1, a2` would
 * otherwise depend on whether the exporter quoted it.
 *
 * Three codes, not four. `SF` is a real relationship in other tools and this
 * product's engine does not model it (contracts: `dependencyTypeSchema`), so a
 * file using one is a rejected row naming the cell rather than a silent
 * downgrade to `FS` -- which would move work and look like it had worked.
 */
export const PREDECESSOR_SEPARATOR = /[\s;]+/;

export interface ParsedPredecessor {
  id: string;
  /** The engine's own union, not a second copy of it. */
  type: DependencyType;
  lagDays: number;
}

const DEPENDENCY_TYPE_BY_CODE: Record<string, DependencyType> = {
  fs: 'finish_to_start',
  ss: 'start_to_start',
  ff: 'finish_to_finish',
};

/**
 * Parses one predecessor reference, or null when it is not one.
 *
 * Returns null rather than throwing, so the caller reports a row error naming
 * the cell instead of failing the whole import on one typo.
 */
export function parsePredecessor(token: string): ParsedPredecessor | null {
  const trimmed = token.trim();
  if (trimmed === '') return null;

  const [id, suffix] = trimmed.split(':', 2);
  if (!id) return null;
  if (suffix === undefined) return { id, type: 'finish_to_start', lagDays: 0 };

  const match = /^([a-zA-Z]{2})([+-]\d+)?$/.exec(suffix.trim());
  if (!match) return null;

  const type = DEPENDENCY_TYPE_BY_CODE[(match[1] ?? '').toLowerCase()];
  if (!type) return null;

  const lagDays = match[2] ? Number(match[2]) : 0;
  if (!Number.isSafeInteger(lagDays)) return null;

  return { id, type, lagDays };
}
